(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const selectAll = (selector, scope = doc) => Array.from(scope.querySelectorAll(selector));
  const liveRegion = doc.querySelector('[data-live-region]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function announce(message) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 30);
  }

  function money(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'en-US', {
        style: 'currency', currency
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  /* Header state */
  const header = doc.querySelector('[data-human-header]');
  if (header) {
    let ticking = false;
    const updateHeader = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
      ticking = false;
    };
    updateHeader();
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      }
    }, { passive: true });
  }

  /* Accessible off-canvas menu and search */
  let activeDrawer = null;
  let activeTrigger = null;
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function drawerByName(name) {
    return selectAll('[data-human-drawer]').find((drawer) => drawer.dataset.humanDrawer === name);
  }

  function closeDrawer(restoreFocus = true) {
    if (!activeDrawer) return;
    const closing = activeDrawer;
    closing.classList.remove('is-open');
    closing.setAttribute('aria-hidden', 'true');
    selectAll('[data-human-drawer-open]').forEach((trigger) => {
      if (trigger.dataset.humanDrawerOpen === closing.dataset.humanDrawer) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
    doc.body.classList.remove('is-drawer-open');
    activeDrawer = null;
    if (restoreFocus && activeTrigger) activeTrigger.focus({ preventScroll: true });
    activeTrigger = null;
  }

  function openDrawer(name, trigger) {
    const drawer = drawerByName(name);
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(false);
    activeDrawer = drawer;
    activeTrigger = trigger || doc.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    doc.body.classList.add('is-drawer-open');
    const first = drawer.querySelector(focusableSelector);
    window.requestAnimationFrame(() => first && first.focus({ preventScroll: true }));
  }

  doc.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-human-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.humanDrawerOpen, opener);
      return;
    }
    const closer = event.target.closest('[data-human-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    const drawerLink = event.target.closest('.human-drawer__nav a');
    if (drawerLink && activeDrawer) closeDrawer(false);
  });

  doc.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = selectAll(focusableSelector, activeDrawer).filter((item) => item.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Quantity steppers */
  selectAll('[data-human-quantity]').forEach((control) => {
    const input = control.querySelector('[data-human-quantity-input]');
    if (!input) return;
    const change = (delta) => {
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const next = Math.min(max, Math.max(min, Number(input.value || min) + delta));
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    control.querySelector('[data-human-quantity-minus]')?.addEventListener('click', () => change(-1));
    control.querySelector('[data-human-quantity-plus]')?.addEventListener('click', () => change(1));
  });

  /* Product gallery */
  selectAll('[data-human-gallery]').forEach((gallery) => {
    selectAll('[data-human-media-thumb]', gallery).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const id = thumb.dataset.humanMediaThumb;
        selectAll('[data-human-media]', gallery).forEach((media) => {
          media.classList.toggle('is-active', media.dataset.humanMedia === id);
        });
        selectAll('[data-human-media-thumb]', gallery).forEach((item) => {
          const current = item === thumb;
          item.classList.toggle('is-active', current);
          item.setAttribute('aria-current', current ? 'true' : 'false');
        });
      });
    });
  });

  /* Product variant state */
  selectAll('[data-product-root]').forEach((productRoot) => {
    const select = productRoot.querySelector('[data-human-variant-select]');
    const json = productRoot.querySelector('[data-human-product-json]');
    if (!select || !json) return;
    let variants = [];
    try { variants = JSON.parse(json.textContent); } catch (_) { return; }
    const update = () => {
      const variant = variants.find((item) => String(item.id) === String(select.value));
      if (!variant) return;
      const currentPrice = productRoot.querySelector('[data-price] .price__current');
      const comparePrice = productRoot.querySelector('[data-price] .price__compare');
      const submit = productRoot.querySelector('[data-human-add-to-cart]');
      if (currentPrice) currentPrice.textContent = money(variant.price);
      if (comparePrice) {
        comparePrice.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : '';
        comparePrice.hidden = !(variant.compare_at_price > variant.price);
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.innerHTML = variant.available
          ? `Add to bag — ${money(variant.price)}`
          : 'Sold out';
      }
      if (variant.featured_media) {
        const mediaButton = productRoot.querySelector(`[data-human-media-thumb="${variant.featured_media.id}"]`);
        if (mediaButton) mediaButton.click();
      }
      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };
    select.addEventListener('change', update);
  });

  async function updateCartCount() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    selectAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
      count.closest('[aria-label]')?.setAttribute('aria-label', `Bag, ${cart.item_count} items`);
    });
    return cart;
  }

  /* Shopify AJAX add-to-cart, with native form fallback when fetch is unavailable */
  selectAll('[data-human-product-form], [data-product-card-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!window.fetch || event.defaultPrevented) return;
      event.preventDefault();
      const button = event.submitter || form.querySelector('[type="submit"]');
      const original = button ? button.innerHTML : '';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding…';
      }
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This form could not be added.');
        await updateCartCount();
        if (button) {
          button.textContent = 'Added ✓';
          button.classList.add('is-added');
        }
        announce(`${result.product_title || 'Item'} added to your bag.`);
        doc.dispatchEvent(new CustomEvent('human:cart:change', { detail: result }));
        window.setTimeout(() => {
          if (!button) return;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.classList.remove('is-added');
          button.innerHTML = original;
        }, 1500);
      } catch (error) {
        announce(error.message || 'Unable to add this item.');
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.innerHTML = original;
        }
      }
    });
  });

  /* Collection and localization controls */
  selectAll('[data-human-sort]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });
  selectAll('[data-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });

  /* Very light hero response on precise pointers only */
  const prism = doc.querySelector('[data-human-prism]');
  if (prism && !reducedMotion && window.matchMedia('(pointer: fine)').matches) {
    const hero = prism.closest('.human-hero');
    hero?.addEventListener('pointermove', (event) => {
      const rect = hero.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - .5) * 18;
      const y = ((event.clientY - rect.top) / rect.height - .5) * 18;
      prism.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }, { passive: true });
    hero?.addEventListener('pointerleave', () => { prism.style.transform = ''; });
  }
})();
